import React from 'react';
import {
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { FormDialog } from '../FormDialog';
import { organizationsApi } from '../../api/services';
import type { Mesh } from '../../api/config';

interface MeshFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; organization_id: number }) => void;
  initialData?: Mesh;
  error?: string | null;
  isPending?: boolean;
}

export const MeshForm: React.FC<MeshFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  error,
  isPending,
}) => {
  const [name, setName] = React.useState('');
  const [organizationId, setOrganizationId] = React.useState<number | ''>('');

  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: organizationsApi.getAll,
    enabled: open,
  });

  React.useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setOrganizationId(initialData.organization_id);
    } else {
      setName('');
      setOrganizationId('');
    }
  }, [initialData, open]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (organizationId === '') return; // required Select; guards the type too
    onSubmit({ name: name.trim(), organization_id: organizationId });
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={handleFormSubmit}
      title={initialData ? 'Rename Mesh' : 'Create Mesh'}
      submitLabel={initialData ? 'Update' : 'Create'}
      error={error}
      isPending={isPending}
    >
      <Box sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Organization</InputLabel>
              <Select
                name="organization_id"
                value={organizationId}
                onChange={(e) => setOrganizationId(Number(e.target.value))}
                label="Organization"
                // A mesh cannot move between orgs once created (the backend
                // ignores organization_id on update): its member locations
                // are validated against this org.
                disabled={!!initialData}
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Mesh Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              helperText="Unique within the organization"
            />
          </Grid>
        </Grid>
      </Box>
    </FormDialog>
  );
};
